import { useEffect, useMemo, useState } from "react";

function PackagingVisual({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id="hx1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.5" />
        </linearGradient>
        <linearGradient id="hx2" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.25" />
        </linearGradient>
      </defs>
      <rect x="48" y="72" width="200" height="140" rx="12" fill="url(#hx1)" opacity="0.35" transform="rotate(-8 148 142)" />
      <rect x="88" y="48" width="180" height="120" rx="10" fill="rgba(15,23,42,0.65)" stroke="rgba(148,163,184,0.35)" strokeWidth="2" />
      <rect x="108" y="68" width="140" height="8" rx="2" fill="rgba(56,189,248,0.45)" />
      <rect x="108" y="88" width="100" height="6" rx="2" fill="rgba(148,163,184,0.35)" />
      <rect x="108" y="102" width="120" height="6" rx="2" fill="rgba(148,163,184,0.22)" />
      <ellipse cx="260" cy="200" rx="56" ry="14" fill="url(#hx2)" opacity="0.6" />
    </svg>
  );
}

export default function HomeHeroPremium({ banners = [], offerLines = [] }) {
  const [idx, setIdx] = useState(0);

  const slides = useMemo(() => {
    if (banners.length > 0) {
      return banners.map((b, i) => ({
        key: b._id || `banner-${i}`,
        type: "image",
        src: b.image,
        alt: b.title || "Promotion"
      }));
    }
    return [
      { key: "fallback-1", type: "fallback", tone: "violet" },
      { key: "fallback-2", type: "fallback", tone: "cyan" },
      { key: "fallback-3", type: "fallback", tone: "amber" }
    ];
  }, [banners]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx((p) => (p + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const go = (delta) => {
    setIdx((p) => (p + delta + slides.length) % slides.length);
  };

  const scrollTo = (id) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const marqueeItems = useMemo(() => {
    const defaults = [ "Free shipping on eligible orders", "Premium materials · Secure delivery"];
    const extra = (offerLines || []).filter(Boolean);
    const merged = [...extra, ...defaults.filter((d) => !extra.includes(d))];
    const unique = [...new Set(merged)];
    return [...unique, ...unique];
  }, [offerLines]);

  return (
    <section className="home-hero-premium" aria-label="Welcome">
      <div className="home-hero-premium__aurora" aria-hidden>
        <span className="home-hero-premium__orb home-hero-premium__orb--a" />
        <span className="home-hero-premium__orb home-hero-premium__orb--b" />
        <span className="home-hero-premium__orb home-hero-premium__orb--c" />
      </div>
      <div className="home-hero-premium__gridlines" aria-hidden />

      <div className="home-hero-premium__copy-block">
        <div className="home-hero-premium__copy-deco" aria-hidden>
          <PackagingVisual className="home-hero-premium__svg-deco" />
        </div>
        <div className="home-hero-premium__copy">
          <span className="home-hero-premium__eyebrow">Corrucraft Industries</span>
          <h1 className="home-hero-premium__title">
            Premium{" "}
            <span className="home-hero-premium__title-accent">Packaging</span> Solutions
          </h1>
          <p className="home-hero-premium__subtitle">
            <span className="home-hero-premium__gradient-text">Corrugated boxes</span>, poly bags, bubble wrap &amp; more
          </p>
          <p className="home-hero-premium__tagline">High-quality materials for secure shipping</p>
          <div className="home-hero-premium__ctas">
            <button type="button" className="home-hero-premium__btn home-hero-premium__btn--primary" onClick={scrollTo("products")}>
              Shop Now
            </button>
            <button type="button" className="home-hero-premium__btn home-hero-premium__btn--ghost" onClick={scrollTo("explore-categories")}>
              Explore Products
            </button>
          </div>
        </div>
      </div>

      <div className="home-hero-premium__banner-full">
        <div className="home-hero-premium__visual-glow home-hero-premium__visual-glow--banner" aria-hidden />
        <div className="home-hero-premium__visual-wrap">
          <div className="home-hero-premium__slider">
            <button type="button" className="home-hero-premium__nav home-hero-premium__nav--prev" aria-label="Previous slide" onClick={() => go(-1)}>
              ‹
            </button>
            <button type="button" className="home-hero-premium__nav home-hero-premium__nav--next" aria-label="Next slide" onClick={() => go(1)}>
              ›
            </button>
            <div className="home-hero-premium__slides">
              {slides.map((slide, i) => (
                <div
                  key={slide.key}
                  className={`home-hero-premium__slide${i === idx ? " is-active" : ""}`}
                  aria-hidden={i !== idx}
                >
                  {slide.type === "image" ? (
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className="home-hero-premium__slide-img"
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  ) : (
                    <div className={`home-hero-premium__fallback home-hero-premium__fallback--${slide.tone}`}>
                      <PackagingVisual className="home-hero-premium__fallback-svg" />
                      <span className="home-hero-premium__fallback-label">Industrial-grade packaging</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="home-hero-premium__dots" role="tablist" aria-label="Hero slides">
              {slides.map((slide, i) => (
                <button
                  key={`dot-${slide.key}`}
                  type="button"
                  role="tab"
                  aria-selected={i === idx}
                  className={i === idx ? "is-active" : ""}
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => setIdx(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="home-hero-premium__marquee" role="region" aria-label="Offers">
        <div className="home-hero-premium__marquee-track">
          {marqueeItems.map((text, i) => (
            <span key={`m-${i}`}>{text}</span>
          ))}
        </div>
      </div>
    </section>
  );
}
